// @greenlight-spec: 41-groups-management.md
// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Users } from 'lucide-react';

import { EmptyState } from '@/components/greenlight/EmptyState';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { thrownCode, thrownMessage } from '@/lib/apiErrors';
import { deriveListQuery } from '@/lib/listQuery';
import type {
  GroupListItem,
  GroupSource,
  IdpGroupRegistration,
  UserListItem,
} from '@greenlight/shared';

import { GroupDetail } from './GroupDetail';
import {
  appCountLabel,
  memberCountLabel,
  mergeGroupRows,
  removalBlockedReason,
  SOURCE_LABEL,
  type GroupRow,
} from './groups-logic';

const PAGE_SIZE = 100;

export interface GroupsSectionProps {
  /** org_admin. An `admin` reads the section; every mutation control is theirs alone. */
  canManage: boolean;
  /** Whether the org's IdP delivers group claims — decides only whether the registry is offered. */
  groupSource?: GroupSource;
  /** The loaded page of users, reused for the member picker and synced IdP membership. */
  users: UserListItem[];
}

/**
 * The `/users` Groups section — where every group an app owner can later pick
 * comes into existence. Both sources render on every org (ADR-0064 decision 4);
 * only the registry form is conditional, since a Google org has no directory
 * groups to register.
 */
export function GroupsSection({
  canManage,
  groupSource,
  users,
}: GroupsSectionProps): React.JSX.Element {
  const queryClient = useQueryClient();
  const offersRegistry = groupSource === 'idp';

  const groupsQuery = api.groups.list.useQuery(['groups'], { query: { limit: PAGE_SIZE } });
  const idpQuery = api.groups.listIdpGroups.useQuery(
    ['idp-groups'],
    { query: { limit: PAGE_SIZE } },
    // The registry is org_admin end to end, reads included (docs/41 §RBAC).
    { queryKey: ['idp-groups'], enabled: canManage && offersRegistry },
  );

  const groups = deriveListQuery<GroupListItem>({
    data: groupsQuery.data,
    isError: groupsQuery.isError,
  });
  const registrations = deriveListQuery<IdpGroupRegistration>({
    data: idpQuery.data,
    isError: idpQuery.isError,
  });

  const rows = React.useMemo(
    () => mergeGroupRows(groups.items, registrations.items),
    [groups.items, registrations.items],
  );
  // Neither list pages yet, so past one page the count is this page's, not the
  // org's — said plainly, as the user list above it does.
  const isTruncated = groups.nextCursor !== null || registrations.nextCursor !== null;

  const isLoading = groupsQuery.isLoading || (canManage && offersRegistry && idpQuery.isLoading);
  const hasError = groups.hasError || registrations.hasError;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
    void queryClient.invalidateQueries({ queryKey: ['idp-groups'] });
    // Membership changes the inspector's group list too.
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  // Dialog state
  const [createOpen, setCreateOpen] = React.useState(false);
  const [registerOpen, setRegisterOpen] = React.useState(false);
  const [editRow, setEditRow] = React.useState<GroupRow | null>(null);
  const [removeRow, setRemoveRow] = React.useState<GroupRow | null>(null);
  const [detailKey, setDetailKey] = React.useState<string | null>(null);
  const [detailOpen, setDetailOpen] = React.useState(false);

  const openDetail = (row: GroupRow): void => {
    setDetailKey(`${row.kind}-${row.id}`);
    setDetailOpen(true);
  };

  // Re-derived every render rather than captured at open time, so a membership
  // edit that refetches the list reaches the drawer too. `lastDetailRow` keeps
  // the panel rendered through its close animation, and through the moment a
  // deleted group leaves the list.
  const detailRow = rows.find((r) => `${r.kind}-${r.id}` === detailKey) ?? null;
  const lastDetailRow = React.useRef<GroupRow | null>(null);
  if (detailRow !== null) lastDetailRow.current = detailRow;

  return (
    <section className="space-y-2.5" data-testid="groups-section">
      <div className="flex flex-wrap items-baseline gap-2 ml-0.5">
        <h2 className="font-mono text-[10.5px] uppercase tracking-[0.08em] font-medium text-foreground">
          Groups
        </h2>
        <span className="font-mono text-[10.5px] text-muted-foreground">{rows.length}</span>
        {canManage ? (
          <span className="hidden text-[11.5px] text-muted-foreground sm:inline">
            What app owners pick from on an app&apos;s Access tab
          </span>
        ) : offersRegistry ? (
          // Not `sm:inline` — an admin has to be able to read what their list
          // leaves out at 360px too.
          <span className="basis-full text-[11.5px] text-muted-foreground sm:basis-auto">
            Greenlight groups only — the directory registry is org admin only
          </span>
        ) : null}
        {canManage && (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              className="h-11 md:h-9"
              onClick={() => setCreateOpen(true)}
            >
              <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              New group
            </Button>
            {offersRegistry && (
              <Button
                variant="outline"
                size="sm"
                className="h-11 md:h-9"
                onClick={() => setRegisterOpen(true)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                Register directory group
              </Button>
            )}
          </div>
        )}
      </div>

      {hasError ? (
        <Alert variant="destructive" data-testid="groups-error">
          <AlertTitle>Couldn&apos;t load groups.</AlertTitle>
          <AlertDescription>
            Something went wrong fetching this org&apos;s groups. Retry in a moment.
          </AlertDescription>
        </Alert>
      ) : isLoading ? (
        <div className="space-y-2" data-testid="groups-loading">
          <Skeleton className="h-16 w-full rounded-gl-lg" />
          <Skeleton className="h-16 w-full rounded-gl-lg" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={canManage || !offersRegistry ? 'No groups yet.' : 'No Greenlight groups yet.'}
          description={
            // An `admin` reads this section but never the registry, which is
            // org_admin end to end (docs/41 §RBAC) — so for them an empty list
            // means "no Greenlight groups", not "no groups", and the register
            // action it would otherwise suggest is not theirs to take.
            !canManage
              ? offersRegistry
                ? 'A group is a named set of people an app owner can admit in one go. Nobody has created a Greenlight group yet; any directory groups your org has registered are visible to org admins only.'
                : 'A group is a named set of people an app owner can admit in one go. Nobody has created one yet.'
              : offersRegistry
                ? 'A group is a named set of people an app owner can admit in one go. Create a Greenlight group for a set your directory does not model, or register a directory group so owners can pick it by name.'
                : 'A group is a named set of people an app owner can admit in one go — create one here, and it becomes pickable on any app’s Access tab.'
          }
          action={
            <Link
              to="/apps"
              className="text-[12.5px] text-foreground underline underline-offset-2 hover:no-underline"
            >
              See where a group gets bound — an app’s Access tab
            </Link>
          }
        />
      ) : (
        <>
          {isTruncated && (
            <p className="text-[11.5px] text-muted-foreground" data-testid="groups-truncated-note">
              This org has more groups than this list holds — showing the first page.
            </p>
          )}
          <ul
            className="overflow-hidden rounded-gl-lg border border-border bg-card divide-y divide-border"
            data-testid="groups-list"
          >
            {rows.map((row) => {
              const blocked = removalBlockedReason(row);
              return (
                <li key={`${row.kind}-${row.id}`} className="flex items-start gap-3 px-4 py-3">
                  <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-gl-icon border border-border bg-muted">
                    <Users className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openDetail(row)}
                        className="truncate text-left text-[12.5px] font-medium text-foreground hover:underline"
                      >
                        {row.name}
                      </button>
                      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-mono text-[10.5px] text-foreground">
                        {SOURCE_LABEL[row.kind]}
                      </span>
                    </div>
                    {row.description !== null && row.description !== '' && (
                      <p className="mt-0.5 text-[12px] text-muted-foreground">{row.description}</p>
                    )}
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                      {memberCountLabel(row.kind, row.memberCount)} · {appCountLabel(row.appCount)}
                    </p>
                  </div>
                  {canManage && (
                    <div className="flex shrink-0 flex-wrap justify-end gap-1.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-11 md:h-8"
                        onClick={() => openDetail(row)}
                      >
                        Members
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-11 md:h-8"
                        onClick={() => setEditRow(row)}
                      >
                        {row.kind === 'idp' ? 'Relabel' : 'Edit'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-11 md:h-8 text-destructive hover:text-destructive"
                        disabled={blocked !== null}
                        title={blocked ?? undefined}
                        onClick={() => setRemoveRow(row)}
                      >
                        {row.kind === 'idp' ? 'Deregister' : 'Delete'}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <CreateGroupDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={invalidate} />
      <RegisterIdpGroupDialog
        open={registerOpen}
        onOpenChange={setRegisterOpen}
        onRegistered={invalidate}
      />
      <EditGroupDialog row={editRow} onClose={() => setEditRow(null)} onSaved={invalidate} />
      <RemoveGroupDialog
        row={removeRow}
        onClose={() => setRemoveRow(null)}
        onRemoved={invalidate}
      />

      <GroupDetail
        row={detailRow ?? lastDetailRow.current}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        canManage={canManage}
        users={users}
        onChanged={invalidate}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

function CreateGroupDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}): React.JSX.Element {
  const create = api.groups.create.useMutation();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [nameError, setNameError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setName('');
      setDescription('');
      setNameError(null);
    }
  }, [open]);

  const submit = async (): Promise<void> => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setNameError(null);
    try {
      const res = await create.mutateAsync({
        body: {
          name: name.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
        },
      });
      if (res.status === 201) {
        toast.success(`Created ${name.trim()}.`);
        onCreated();
        onOpenChange(false);
      } else {
        setNameError("Couldn't create that group.");
      }
    } catch (err) {
      if (thrownCode(err) === 'groups.name_conflict') {
        setNameError('A group with that name already exists.');
      } else {
        setNameError(thrownMessage(err, "Couldn't create that group."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[18px] ad-shadow">
        <DialogHeader>
          <DialogTitle>New group</DialogTitle>
          <DialogDescription>
            A named set of people. App owners admit the whole group in one go from an app&apos;s
            Access tab.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="group-name">Name</Label>
            <Input
              id="group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Finance"
              className="h-11 md:h-9"
              aria-invalid={nameError !== null}
              aria-describedby={nameError !== null ? 'group-name-error' : undefined}
            />
            {nameError !== null && (
              <p id="group-name-error" className="text-[12px] text-destructive">
                {nameError}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="group-description">Description (optional)</Label>
            <Textarea
              id="group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Who belongs here, and what it gates."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!name.trim() || saving}>
            {saving ? 'Creating…' : 'Create group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Register a directory group
// ---------------------------------------------------------------------------

function RegisterIdpGroupDialog({
  open,
  onOpenChange,
  onRegistered,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegistered: () => void;
}): React.JSX.Element {
  const register = api.groups.registerIdpGroup.useMutation();
  const [identifier, setIdentifier] = React.useState('');
  const [label, setLabel] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [identifierError, setIdentifierError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setIdentifier('');
      setLabel('');
      setDescription('');
      setIdentifierError(null);
    }
  }, [open]);

  const submit = async (): Promise<void> => {
    if (!identifier.trim() || !label.trim() || saving) return;
    setSaving(true);
    setIdentifierError(null);
    try {
      const res = await register.mutateAsync({
        body: {
          idp_group_id: identifier.trim(),
          label: label.trim(),
          ...(description.trim() ? { description: description.trim() } : {}),
        },
      });
      if (res.status === 201) {
        toast.success(`Registered ${label.trim()}.`);
        onRegistered();
        onOpenChange(false);
      } else {
        setIdentifierError("Couldn't register that group.");
      }
    } catch (err) {
      if (thrownCode(err) === 'idp_groups.already_registered') {
        setIdentifierError('That group is already registered.');
      } else {
        setIdentifierError(thrownMessage(err, "Couldn't register that group."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[18px] ad-shadow">
        <DialogHeader>
          <DialogTitle>Register a directory group</DialogTitle>
          <DialogDescription>
            Paste the group&apos;s identifier from your identity provider once and give it a name.
            App owners then pick it by that name and never see the identifier.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="idp-group-identifier">Group identifier</Label>
            <Input
              id="idp-group-identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder="00000000-0000-0000-0000-000000000000"
              className="h-11 md:h-9 font-mono text-[12px]"
              aria-invalid={identifierError !== null}
              aria-describedby={identifierError !== null ? 'idp-group-identifier-error' : undefined}
            />
            {identifierError !== null && (
              <p id="idp-group-identifier-error" className="text-[12px] text-destructive">
                {identifierError}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="idp-group-label">Name</Label>
            <Input
              id="idp-group-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Finance (Entra)"
              className="h-11 md:h-9"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="idp-group-description">Description (optional)</Label>
            <Textarea
              id="idp-group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            disabled={!identifier.trim() || !label.trim() || saving}
          >
            {saving ? 'Registering…' : 'Register group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Edit / relabel
// ---------------------------------------------------------------------------

function EditGroupDialog({
  row,
  onClose,
  onSaved,
}: {
  row: GroupRow | null;
  onClose: () => void;
  onSaved: () => void;
}): React.JSX.Element {
  const update = api.groups.update.useMutation();
  const updateIdp = api.groups.updateIdpGroup.useMutation();
  const [name, setName] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setName(row?.name ?? '');
    setDescription(row?.description ?? '');
    setError(null);
  }, [row]);

  const submit = async (): Promise<void> => {
    if (row === null || !name.trim() || saving) return;
    setSaving(true);
    setError(null);
    const body = {
      description: description.trim() === '' ? null : description.trim(),
    };
    try {
      const res =
        row.kind === 'idp'
          ? await updateIdp.mutateAsync({
              params: { id: row.id },
              body: { label: name.trim(), ...body },
            })
          : await update.mutateAsync({
              params: { id: row.id },
              body: { name: name.trim(), ...body },
            });
      if (res.status === 200) {
        toast.success('Group updated.');
        onSaved();
        onClose();
      } else {
        setError("Couldn't save that change.");
      }
    } catch (err) {
      if (thrownCode(err) === 'groups.name_conflict') {
        setError('A group with that name already exists.');
      } else {
        setError(thrownMessage(err, "Couldn't save that change."));
      }
    } finally {
      setSaving(false);
    }
  };

  const isIdp = row?.kind === 'idp';

  return (
    <Dialog open={row !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="rounded-[18px] ad-shadow">
        <DialogHeader>
          <DialogTitle>{isIdp ? 'Relabel directory group' : 'Edit group'}</DialogTitle>
          <DialogDescription>
            {isIdp
              ? 'The identifier is fixed — a different group is a different registration.'
              : 'Renaming is safe: apps bind to the group itself, not its name.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="edit-group-name">Name</Label>
            <Input
              id="edit-group-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="h-11 md:h-9"
              aria-invalid={error !== null}
              aria-describedby={error !== null ? 'edit-group-error' : undefined}
            />
            {error !== null && (
              <p id="edit-group-error" className="text-[12px] text-destructive">
                {error}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-group-description">Description</Label>
            <Textarea
              id="edit-group-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!name.trim() || saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Delete / deregister
// ---------------------------------------------------------------------------

function RemoveGroupDialog({
  row,
  onClose,
  onRemoved,
}: {
  row: GroupRow | null;
  onClose: () => void;
  onRemoved: () => void;
}): React.JSX.Element {
  const remove = api.groups.remove.useMutation();
  const deregister = api.groups.deregisterIdpGroup.useMutation();
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    setError(null);
  }, [row]);

  const isIdp = row?.kind === 'idp';

  const submit = async (): Promise<void> => {
    if (row === null || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = isIdp
        ? await deregister.mutateAsync({ params: { id: row.id } })
        : await remove.mutateAsync({ params: { id: row.id } });
      if (res.status === 204) {
        toast.success(isIdp ? `Deregistered ${row.name}.` : `Deleted ${row.name}.`);
        onRemoved();
        onClose();
      } else {
        setError("Couldn't complete that.");
      }
    } catch (err) {
      // The list's disabled control is a courtesy; a binding added since the
      // page loaded lands here instead.
      if (thrownCode(err) === 'groups.in_use') {
        setError(
          `${row.name} is bound to at least one app. Remove it from that app's Access tab first.`,
        );
      } else {
        setError(thrownMessage(err, "Couldn't complete that."));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={row !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="rounded-[18px] ad-shadow">
        <DialogHeader>
          <DialogTitle>{isIdp ? `Deregister ${row?.name}?` : `Delete ${row?.name}?`}</DialogTitle>
          <DialogDescription>
            {isIdp
              ? 'App owners will no longer be able to pick this group. The group itself is untouched in your directory.'
              : 'This deletes the group and its membership. People keep any access they hold in their own right.'}
          </DialogDescription>
        </DialogHeader>
        {error !== null && <p className="text-[12px] text-destructive">{error}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={() => void submit()} disabled={saving}>
            {saving ? 'Working…' : isIdp ? 'Deregister' : 'Delete group'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
